import React, { useCallback } from "react";
import Dropzone from "react-dropzone";
import { useQboAttachments } from "../../../hooks/business_integrations/qbo/useQboAttachments";
import { IQboAttachment } from "../../../models/DTO/IQboAttachmentDTO";
import AttachmentListItem from "../../order_entry/OrderEntryFileManager/AttachmentListItem/AttachmentListItem";
import OwTranslate from "../OwTranslate/OwTranslate";
import OwToggle from "../OwToggle/OwToggle";
import AttachmentTypeEnum from "../../../models/AttachmentTypeEnum";
import IAttachment from "../../../models/IAttachment";
import "./QboAttachmentDropzone.scss";

interface QboAttachmentDropzoneProps {
    qboEntityId: string;
    accountingId?: number;
    isReadOnly?: boolean;
    onUploadComplete?: (attachment: IQboAttachment) => void;
    onDeleteComplete?: (attachmentGuid: string) => void;
    onError?: (error: string) => void;
    maxFileSizeMb?: number;
}

const MAX_FILE_SIZE_DEFAULT = 100; // MB

export const QboAttachmentDropzone: React.FC<QboAttachmentDropzoneProps> = ({
    qboEntityId,
    accountingId,
    isReadOnly = false,
    onUploadComplete,
    onDeleteComplete,
    onError,
    maxFileSizeMb = MAX_FILE_SIZE_DEFAULT,
}) => {
    const {
        attachments,
        isLoading,
        isUploading,
        uploadProgress,
        error,
        includeOnSend,
        setIncludeOnSend,
        uploadFile,
        deleteAttachment,
        updateAttachment,
        downloadAttachment,
        clearError,
    } = useQboAttachments({
        qboEntityId,
        accountingId,
        onUploadComplete,
        onDeleteComplete,
        onError,
    });

    const maxSizeBytes = maxFileSizeMb * 1024 * 1024;

    const handleDrop = useCallback(
        (acceptedFiles: File[]) => {
            if (acceptedFiles.length > 0) {
                clearError();
                uploadFile(acceptedFiles[0]);
            }
        },
        [uploadFile, clearError]
    );

    const handleDropRejected = useCallback(
        (fileRejections: any[]) => {
            if (fileRejections.length > 0) {
                const rejection = fileRejections[0];
                if (rejection.errors?.[0]?.code === "file-too-large") {
                    onError?.(`File exceeds maximum size of ${maxFileSizeMb}MB`);
                } else {
                    onError?.("File rejected");
                }
            }
        },
        [maxFileSizeMb, onError]
    );

    const handleDownload = useCallback(
        (attachmentGuid: string) => {
            const attachment = attachments.find((a) => a.guid === attachmentGuid);
            if (attachment) {
                downloadAttachment(attachment);
            }
        },
        [attachments, downloadAttachment]
    );

    const handleDelete = useCallback(
        (attachmentGuid: string) => {
            deleteAttachment(attachmentGuid);
        },
        [deleteAttachment]
    );

    const handleIncludeOnSendToggle = useCallback(
        (e: any) => {
            setIncludeOnSend(e.target.value);
        },
        [setIncludeOnSend]
    );

    const handleAttachmentIncludeOnSendToggle = useCallback(
        (attachmentGuid: string, newValue: boolean) => {
            updateAttachment(attachmentGuid, newValue);
        },
        [updateAttachment]
    );

    const renderIncludeOnSendToggle = () => {
        if (isReadOnly) return null;

        return (
            <div className="include-on-send-toggle mb-2">
                <OwToggle name="includeOnSend" label="include_on_send" active={includeOnSend} fn={handleIncludeOnSendToggle} />
            </div>
        );
    };

    const renderDropzone = () => {
        if (isReadOnly) return null;

        return (
            <Dropzone
                maxSize={maxSizeBytes}
                multiple={false}
                onDrop={handleDrop}
                onDropRejected={handleDropRejected}
                disabled={isUploading}>
                {({ getRootProps, getInputProps, isDragActive }) => (
                    <section className={`dropzone-section ${isDragActive ? "drag-active" : ""}`}>
                        <div {...getRootProps()}>
                            <input {...getInputProps()} />
                            <p className="dropzone-content">
                                {isUploading ? (
                                    <span>
                                        <i className="fas fa-spinner fa-spin me-2" />
                                        <OwTranslate defaultMessage="Uploading..." id="uploading" />
                                    </span>
                                ) : (
                                    <span>
                                        <i className="fas fa-cloud-upload-alt me-2" />
                                        <OwTranslate defaultMessage="Drag and drop or click to select" id="drag_and_drop_or_click" />
                                    </span>
                                )}
                            </p>
                        </div>
                    </section>
                )}
            </Dropzone>
        );
    };

    const renderProgressBar = () => {
        if (!isUploading) return null;

        return (
            <div className="upload-progress-section mt-2">
                <div className="upload-progress-bar">
                    <div className="bar" style={{ width: `${uploadProgress}%` }} />
                </div>
                <span className="text-muted text-small">{uploadProgress}%</span>
            </div>
        );
    };

    const renderError = () => {
        if (!error) return null;

        return (
            <div className="error-section mt-2 text-danger">
                <i className="fas fa-exclamation-triangle me-1" />
                {error}
            </div>
        );
    };

    const renderAttachmentsList = () => {
        if (isLoading) {
            return (
                <div className="text-muted mt-2">
                    <i className="fas fa-spinner fa-spin me-2" />
                    <OwTranslate defaultMessage="Loading..." id="loading" />
                </div>
            );
        }

        if (attachments.length === 0) {
            return (
                <div className="text-muted mt-2 text-small">
                    <OwTranslate defaultMessage="No attachments" id="no_attachments" />
                </div>
            );
        }

        return (
            <div className="attachments-list mt-2">
                {attachments.map((attachment) => (
                    <div key={attachment.guid} className="attachment-item-wrapper">
                        <div className="d-flex align-items-center">
                            <AttachmentListItem
                                attachment={
                                    {
                                        guid: attachment.guid,
                                        name: attachment.name,
                                        size: attachment.size,
                                        url: attachment.signedUrl || "",
                                        key: "",
                                        bucket: "",
                                        etag: "",
                                        supplier_id: 0,
                                        user_id: 0,
                                        attachment_type_id: AttachmentTypeEnum.pdf,
                                    } as IAttachment
                                }
                                isReadOnly={isReadOnly}
                                downloadAttachment={handleDownload}
                                deleteAttachment={handleDelete}
                            />
                            {!isReadOnly && (
                                <div className="ms-2">
                                    <OwToggle
                                        name={`includeOnSend-${attachment.guid}`}
                                        label="include_on_send"
                                        active={attachment.qbo_includeOnSend || false}
                                        fn={() => handleAttachmentIncludeOnSendToggle(attachment.guid, !attachment.qbo_includeOnSend)}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="QboAttachmentDropzone">
            {renderIncludeOnSendToggle()}
            {renderDropzone()}
            {renderProgressBar()}
            {renderError()}
            {renderAttachmentsList()}
        </div>
    );
};

export default QboAttachmentDropzone;
